import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { companiesApi, usersApi } from '../api';
import { ArrowLeft, Pencil, Save, X, Trash2, Plus, UserCircle, KanbanSquare, CheckSquare } from 'lucide-react';
import SearchableSelect from '../components/SearchableSelect';
import ContactModal from '../components/ContactModal';
import DealModal from '../components/DealModal';
import TaskModal from '../components/TaskModal';
import dayjs from 'dayjs';

const typeLabels: Record<string, string> = { CUSTOMER: 'Клиент', PARTNER: 'Партнёр', VENDOR: 'Поставщик', OTHER: 'Другое' };
const stageLabels: Record<string, string> = { LEAD: 'Новые', NEGOTIATION: 'Переговоры', PROPOSAL: 'Предложение', AGREEMENT: 'Согласование', WON: 'Успешно', LOST: 'Проиграна' };
const stageBadge: Record<string, string> = { LEAD: 'badge-info', NEGOTIATION: 'badge-warning', PROPOSAL: 'badge-warning', AGREEMENT: 'badge-default', WON: 'badge-success', LOST: 'badge-danger' };
const statusLabel: Record<string, string> = { PENDING: 'Ожидает', IN_PROGRESS: 'В работе', COMPLETED: 'Выполнена', CANCELLED: 'Отменена' };
const statusColor: Record<string, string> = { PENDING: 'badge-warning', IN_PROGRESS: 'badge-info', COMPLETED: 'badge-success', CANCELLED: 'badge-default' };

export default function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showDealModal, setShowDealModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await companiesApi.getById(id);
      setCompany(res.data);
      setForm({
        name: res.data.name || '',
        inn: res.data.inn || '',
        type: res.data.type || 'CUSTOMER',
        industry: res.data.industry || '',
        website: res.data.website || '',
        phones: res.data.phones?.join(', ') || '',
        emails: res.data.emails?.join(', ') || '',
        address: res.data.address || '',
        description: res.data.description || '',
        tags: res.data.tags?.join(', ') || '',
        responsibleId: res.data.responsibleId || '',
      });
    } catch (e) {
      console.error('Load company error:', e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await usersApi.list({ limit: 100 });
        setUsers(res.data.data || []);
      } catch {}
    };
    loadUsers();
  }, []);

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const data = {
        ...form,
        inn: form.inn || null,
        phones: form.phones.split(',').map((s: string) => s.trim()).filter(Boolean),
        emails: form.emails.split(',').map((s: string) => s.trim()).filter(Boolean),
        tags: form.tags.split(',').map((s: string) => s.trim()).filter(Boolean),
        responsibleId: form.responsibleId || null,
      };
      await companiesApi.update(id, data);
      setEditing(false);
      load();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !confirm('Удалить компанию?')) return;
    try {
      await companiesApi.remove(id);
      navigate('/companies');
    } catch (e) {
      alert('Ошибка удаления');
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Загрузка...</div>;
  if (!company) return <div style={{ padding: 40, textAlign: 'center' }}>Компания не найдена</div>;

  return (
    <div>
      <div className="detail-header">
        <button className="detail-back-btn" onClick={() => navigate('/companies')}>
          <ArrowLeft size={20} />
        </button>
        <div className="detail-header-info">
          <div className="detail-header-title">{company.name}</div>
          <div className="detail-header-subtitle">
            <span className="badge badge-default">{typeLabels[company.type] || company.type}</span>
            {company.industry && <span style={{ marginLeft: 8, color: 'var(--color-text-secondary)' }}>{company.industry}</span>}
          </div>
        </div>
        <div className="detail-header-actions">
          {editing ? (
            <>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                <Save size={16} /> {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(false); load(); }}>
                <X size={16} /> Отмена
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
                <Pencil size={16} /> Редактировать
              </button>
              <button className="btn btn-danger btn-sm" onClick={handleDelete}>
                <Trash2 size={16} /> Удалить
              </button>
            </>
          )}
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-grid-main">
          <div className="card">
            <div className="card-header">
              <div className="card-title">Информация о компании</div>
            </div>
            <div className="card-body">
              {editing ? (
                <>
                  <div className="detail-edit-row">
                    <div className="form-group">
                      <label className="form-label">Название *</label>
                      <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">ИНН</label>
                      <input className="form-input" value={form.inn} onChange={e => setForm({ ...form, inn: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row">
                    <div className="form-group">
                      <label className="form-label">Тип</label>
                      <select className="form-select" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                        <option value="CUSTOMER">Клиент</option>
                        <option value="PARTNER">Партнер</option>
                        <option value="VENDOR">Поставщик</option>
                        <option value="OTHER">Другое</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Отрасль</label>
                      <input className="form-input" value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row single">
                    <div className="form-group">
                      <label className="form-label">Сайт</label>
                      <input className="form-input" value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row">
                    <div className="form-group">
                      <label className="form-label">Телефоны (через запятую)</label>
                      <input className="form-input" value={form.phones} onChange={e => setForm({ ...form, phones: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email (через запятую)</label>
                      <input className="form-input" value={form.emails} onChange={e => setForm({ ...form, emails: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row single">
                    <div className="form-group">
                      <label className="form-label">Адрес</label>
                      <input className="form-input" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row single">
                    <div className="form-group">
                      <label className="form-label">Описание</label>
                      <textarea className="form-input" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row single">
                    <div className="form-group">
                      <label className="form-label">Теги (через запятую)</label>
                      <input className="form-input" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row single">
                    <div className="form-group">
                      <SearchableSelect
                        label="Ответственный"
                        value={form.responsibleId}
                        onChange={val => setForm({ ...form, responsibleId: val })}
                        options={users.map((u: any) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }))}
                        emptyLabel="— Не назначен —"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="detail-field-grid">
                  <div className="detail-field">
                    <div className="detail-field-label">Название</div>
                    <div className="detail-field-value">{company.name}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">ИНН</div>
                    <div className="detail-field-value">{company.inn || <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Тип</div>
                    <div className="detail-field-value"><span className="badge badge-default">{typeLabels[company.type] || company.type}</span></div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Отрасль</div>
                    <div className="detail-field-value">{company.industry || <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Сайт</div>
                    <div className="detail-field-value">{company.website ? <a href={company.website} target="_blank" rel="noopener noreferrer" className="detail-link">{company.website}</a> : <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Адрес</div>
                    <div className="detail-field-value">{company.address || <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Телефоны</div>
                    <div className="detail-field-value">{company.phones?.length ? company.phones.join(', ') : <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Email</div>
                    <div className="detail-field-value">{company.emails?.length ? company.emails.join(', ') : <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field detail-field-full">
                    <div className="detail-field-label">Описание</div>
                    <div className="detail-field-value">{company.description || <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field detail-field-full">
                    <div className="detail-field-label">Теги</div>
                    <div className="detail-field-value">
                      {company.tags?.length ? (
                        <div className="detail-tags">{company.tags.map((t: string, i: number) => <span key={i} className="detail-tag">{t}</span>)}</div>
                      ) : <span className="empty">—</span>}
                    </div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Ответственный</div>
                    <div className="detail-field-value">{company.responsible ? `${company.responsible.firstName} ${company.responsible.lastName}` : <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Создано</div>
                    <div className="detail-field-value">{dayjs(company.createdAt).format('DD.MM.YYYY HH:mm')}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="detail-grid-sidebar">
          <div className="card">
            <div className="card-header">
              <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <UserCircle size={16} /> Контакты ({company.contacts?.length || 0})
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowContactModal(true)}>
                <Plus size={14} /> Добавить
              </button>
            </div>
            <div className="card-body" style={{ padding: company.contacts?.length ? 12 : 24 }}>
              {company.contacts?.length ? company.contacts.map((c: any) => (
                <Link key={c.id} to={`/contacts/${c.id}`} className="detail-related-item">
                  <div className="detail-related-item-info">
                    <div className="detail-related-item-title">{c.firstName} {c.lastName}</div>
                    <div className="detail-related-item-meta">{c.position || c.emails?.[0] || '—'}</div>
                  </div>
                </Link>
              )) : <div className="detail-related-empty">Нет контактов</div>}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <KanbanSquare size={16} /> Сделки ({company.deals?.length || 0})
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowDealModal(true)}>
                <Plus size={14} /> Добавить
              </button>
            </div>
            <div className="card-body" style={{ padding: company.deals?.length ? 12 : 24 }}>
              {company.deals?.length ? company.deals.map((d: any) => (
                <Link key={d.id} to={`/deals/${d.id}`} className="detail-related-item">
                  <div className="detail-related-item-info">
                    <div className="detail-related-item-title">{d.title}</div>
                    <div className="detail-related-item-meta">{d.amount ? Number(d.amount).toLocaleString('ru-RU') + ' \u20BD' : '—'}</div>
                  </div>
                  <div className="detail-related-item-badge">
                    <span className={`badge ${stageBadge[d.stage] || 'badge-default'}`}>{stageLabels[d.stage] || d.stage}</span>
                  </div>
                </Link>
              )) : <div className="detail-related-empty">Нет сделок</div>}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckSquare size={16} /> Задачи ({[...(company.tasks || []), ...(company.participantTasks || [])].filter((t: any, i: number, arr: any[]) => arr.findIndex((x: any) => x.id === t.id) === i).length})
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowTaskModal(true)}>
                <Plus size={14} /> Добавить
              </button>
            </div>
            <div className="card-body" style={{ padding: company.tasks?.length || company.participantTasks?.length ? 12 : 24 }}>
              {[...(company.tasks || []), ...(company.participantTasks || [])]
                .filter((t: any, i: number, arr: any[]) => arr.findIndex((x: any) => x.id === t.id) === i)
                .sort((a: any, b: any) => {
                  if (a.dueDate && b.dueDate) return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
                  if (a.dueDate) return -1;
                  if (b.dueDate) return 1;
                  return 0;
                })
                .map((t: any) => (
                <Link key={t.id} to={`/tasks/${t.id}`} className="detail-related-item">
                  <div className="detail-related-item-info">
                    <div className="detail-related-item-title" style={{ textDecoration: t.isCompleted ? 'line-through' : 'none' }}>{t.title}</div>
                    <div className="detail-related-item-meta">
                      {t.dueDate ? dayjs(t.dueDate).format('DD.MM.YYYY') : '—'} · {t.assignee?.firstName} {t.assignee?.lastName}
                    </div>
                  </div>
                  <div className="detail-related-item-badge">
                    <span className={`badge ${statusColor[t.status] || 'badge-default'}`}>{statusLabel[t.status] || t.status}</span>
                  </div>
                </Link>
              ))}
              {!company.tasks?.length && !company.participantTasks?.length && (
                <div className="detail-related-empty">Нет задач</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showContactModal && (
        <ContactModal
          contact={{ companyId: id }}
          onClose={() => setShowContactModal(false)}
          onSave={() => { setShowContactModal(false); load(); }}
        />
      )}
      {showDealModal && (
        <DealModal
          deal={{ companyId: id }}
          onClose={() => setShowDealModal(false)}
          onSave={() => { setShowDealModal(false); load(); }}
        />
      )}
      {showTaskModal && (
        <TaskModal
          companyId={id}
          onClose={() => setShowTaskModal(false)}
          onSave={() => { setShowTaskModal(false); load(); }}
        />
      )}
    </div>
  );
}
